/**
 * Shared polling for AI Impact Assessment Results modal via
 * POST /assessment/regulation-assessment-status
 */

import {
  classifyAiImpactAssessmentScope,
  postRegulationAssessmentStatus,
  type AssessmentBy,
  type AssessmentBundleTaskStatusData,
  type AssessmentRegulation,
} from "../../services/assessmentService";
import type { AiResultsModalRow } from "../../components/AssessmentCenterPage";
import { coerceToTriageImpactedItems } from "../../services/manualAssessmentService";

const TERMINAL_STATUS = new Set(["success", "failed", "partial", "completed"]);

export const REGULATION_ASSESSMENT_POLL_INTERVAL_MS = 5000;
export const REGULATION_ASSESSMENT_MAX_POLLS = 60;

function normalizeUiStatus(raw: string): string {
  const low = raw.toLowerCase();
  if (low === "success" || low === "completed") return "Success";
  if (low === "failed" || low === "failure") return "Failed";
  if (low === "partial") return "Partial";
  if (low === "running" || low === "init") return "Running";
  return raw;
}

function versionKey(version: string | undefined): string {
  if (!version) return "";
  const t = version.trim();
  return t.startsWith("v") || t.startsWith("V") ? t.slice(1) : t;
}

function regulationRowMatchKey(regulationId: string, version: string): string {
  return `${regulationId.trim()}:${versionKey(version)}`;
}

/** `by` for regulation-assessment-status after re-run in the AI Results modal. */
export function pollByForAiResultsRow(assessmentType: string): AssessmentBy {
  return classifyAiImpactAssessmentScope(assessmentType) === "system"
    ? "system"
    : "user";
}

export function aiResultsRowsToAssessmentRegulations(
  rows: readonly AiResultsModalRow[],
): AssessmentRegulation[] {
  return rows.map((row) => ({
    id: row.regulationId,
    version: row.version,
    title: row.regulation || row.regulationId,
    documents: [],
  }));
}

/** Reset one modal row to Running after the user starts a re-run. */
export function markAiResultsRowsForRerun(
  rows: AiResultsModalRow[],
  retriedRow: Pick<AiResultsModalRow, "regulationId" | "version">,
): AiResultsModalRow[] {
  const want = regulationRowMatchKey(retriedRow.regulationId, retriedRow.version);
  return rows.map((row) => {
    if (regulationRowMatchKey(row.regulationId, row.version) !== want) {
      return row;
    }
    return {
      ...row,
      rawStatus: "running",
      status: "Running",
      impactLevel: "Pending",
      reportUrl: null,
      failedConceptNames: undefined,
    };
  });
}

function getRegulationAssessmentPollItems(
  pollData: AssessmentBundleTaskStatusData | undefined | null,
): Record<string, unknown>[] {
  if (!pollData) return [];
  const fromItems = pollData.items;
  if (Array.isArray(fromItems)) {
    return fromItems.filter((x): x is Record<string, unknown> => x != null && typeof x === "object");
  }
  const regs = pollData.regulations;
  if (Array.isArray(regs)) {
    return regs.filter((x): x is Record<string, unknown> => x != null && typeof x === "object");
  }
  return [];
}

function findPollItemForRow(
  row: AiResultsModalRow,
  pollItems: Record<string, unknown>[],
): Record<string, unknown> | undefined {
  const want = regulationRowMatchKey(row.regulationId, row.version);
  return pollItems.find((item) => {
    const id = String(item.regulation_id ?? item.id ?? "").trim();
    const ver = String(item.regulation_version ?? item.version ?? "").trim();
    if (!id) return false;
    return regulationRowMatchKey(id, ver || "1.0") === want;
  });
}

function formatPollExecutedDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
      .format(d)
      .replace(/\s+/g, "-");
  } catch {
    return iso;
  }
}

function formatImpactedItemsFromPoll(impactedItems: unknown): string {
  if (!Array.isArray(impactedItems) || impactedItems.length === 0) return "—";
  const parts: string[] = [];
  for (const raw of impactedItems) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const keyword = typeof o.keyword === "string" ? o.keyword.trim() : "";
    const cv =
      o.concept_value && typeof o.concept_value === "object"
        ? String((o.concept_value as Record<string, unknown>).name ?? "").trim()
        : "";
    const cn =
      o.concept && typeof o.concept === "object"
        ? String((o.concept as Record<string, unknown>).name ?? "").trim()
        : "";
    const label = cv || keyword || cn;
    if (label) parts.push(label);
  }
  return parts.length > 0 ? parts.join(", ") : "—";
}

export function mergeRegulationAssessmentPollIntoRows(
  prevRows: AiResultsModalRow[],
  pollData: AssessmentBundleTaskStatusData | undefined | null,
): AiResultsModalRow[] {
  if (!pollData) return prevRows;

  const bundleStatus = String(pollData.status ?? "").toLowerCase();
  const pollItems = getRegulationAssessmentPollItems(pollData);

  if (pollItems.length > 0) {
    return prevRows.map((row) => {
      const match = findPollItemForRow(row, pollItems);
      if (!match) return row;

      const rawStatus = String(
        match.status ?? match.regulation_status ?? "",
      ).toLowerCase();
      const rowStatus = normalizeUiStatus(
        rawStatus || bundleStatus || "running",
      );
      const failedNames = Array.isArray(match.failed_concept_names)
        ? (match.failed_concept_names as unknown[]).filter(
            (n): n is string => typeof n === "string" && n.trim().length > 0,
          )
        : row.failedConceptNames;

      const reportRaw = match.report_url ?? match.reportUrl ?? match.file_path;
      const reportUrl =
        typeof reportRaw === "string" && reportRaw.trim() ? reportRaw.trim() : row.reportUrl;

      let impactLevel = row.impactLevel;
      const il = match.impacted_level ?? match.impact_level ?? match.impactLevel;
      if (typeof il === "string" && il.trim()) {
        impactLevel = il.trim();
      }

      let impactedConcepts = row.impactedConcepts;
      const rawImpactedItems = match.impacted_items ?? match.impactedItems;
      let impactedItems = row.impactedItems;
      if (Array.isArray(rawImpactedItems) && rawImpactedItems.length > 0) {
        impactedConcepts = formatImpactedItemsFromPoll(rawImpactedItems);
        impactedItems = coerceToTriageImpactedItems(rawImpactedItems);
      } else if (match.impacted === false) {
        impactedConcepts = "—";
        impactedItems = [];
      }

      const execName = match.executor_name ?? match.executed_by;
      const executedBy =
        typeof execName === "string" && execName.trim() ? execName.trim() : row.executedBy;

      const executedAtRaw = match.executed_at ?? match.executedAt;
      const executedDate =
        typeof executedAtRaw === "string" && executedAtRaw.trim()
          ? formatPollExecutedDate(executedAtRaw.trim())
          : row.executedDate;

      const titleRaw = match.regulation_title;
      const regulation =
        typeof titleRaw === "string" && titleRaw.trim()
          ? titleRaw.trim()
          : row.regulation;

      return {
        ...row,
        regulation,
        rawStatus: rawStatus || row.rawStatus,
        status: rowStatus,
        impactLevel,
        reportUrl,
        impactedConcepts,
        impactedItems,
        executedBy,
        executedDate,
        ...(failedNames && failedNames.length > 0
          ? { failedConceptNames: failedNames }
          : {}),
      };
    });
  }

  if (
    bundleStatus === "running" ||
    bundleStatus === "init" ||
    bundleStatus === ""
  ) {
    return prevRows.map((r) =>
      r.status === "Running"
        ? r
        : { ...r, rawStatus: "running", status: "Running" },
    );
  }

  if (TERMINAL_STATUS.has(bundleStatus)) {
    // Bundle-level terminal without per-reg items is unreliable for multi-reg batches.
    if (pollItems.length === 0 && prevRows.length > 1) {
      return prevRows;
    }
    const rowStatus = normalizeUiStatus(bundleStatus);
    return prevRows.map((r) => ({
      ...r,
      rawStatus: bundleStatus,
      status: rowStatus,
      impactLevel: rowStatus === "Success" ? "—" : r.impactLevel,
    }));
  }

  return prevRows;
}

function isTerminalPollStatus(status: string): boolean {
  const low = status.toLowerCase();
  return TERMINAL_STATUS.has(low);
}

export function shouldStopRegulationAssessmentPoll(
  expectedRegulationCount: number,
  pollData: AssessmentBundleTaskStatusData | undefined | null,
): boolean {
  if (!pollData) return false;
  const pollItems = getRegulationAssessmentPollItems(pollData);
  if (pollItems.length === 0) {
    // Multi-reg: keep polling until items[] is populated (top-level status alone is not enough).
    if (expectedRegulationCount > 1) return false;
    return isTerminalPollStatus(String(pollData.status ?? ""));
  }
  const hasInProgress = pollItems.some(
    (it) => !isTerminalPollStatus(String(it.status ?? "")),
  );
  if (hasInProgress) return false;
  if (expectedRegulationCount <= 0) return true;
  return pollItems.length >= expectedRegulationCount;
}

export interface PollRegulationAssessmentStatusParams {
  taskUuid: string;
  regulations: AssessmentRegulation[];
  by: "user" | "system";
  updateAiResultsModalRows: (
    rowsOrUpdater:
      | AiResultsModalRow[]
      | ((previous: AiResultsModalRow[]) => AiResultsModalRow[]),
  ) => void;
  setAiResultsTableLoading: (loading: boolean) => void;
  refreshAssessmentCenter: () => void;
  isCancelled: () => boolean;
}

export async function pollRegulationAssessmentStatusUntilDone({
  taskUuid,
  regulations,
  by,
  updateAiResultsModalRows,
  setAiResultsTableLoading,
  refreshAssessmentCenter,
  isCancelled,
}: PollRegulationAssessmentStatusParams): Promise<void> {
  setAiResultsTableLoading(true);
  try {
    for (let attempt = 0; attempt < REGULATION_ASSESSMENT_MAX_POLLS; attempt += 1) {
      if (isCancelled()) {
        return;
      }

      if (attempt > 0) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, REGULATION_ASSESSMENT_POLL_INTERVAL_MS),
        );
        if (isCancelled()) {
          return;
        }
      }

      const response = await postRegulationAssessmentStatus({
        task_uuid: taskUuid,
        by,
        regulations,
      });

      if (isCancelled()) {
        return;
      }

      updateAiResultsModalRows((prev) =>
        mergeRegulationAssessmentPollIntoRows(prev, response.data),
      );

      if (shouldStopRegulationAssessmentPoll(regulations.length, response.data)) {
        refreshAssessmentCenter();
        return;
      }
    }

    if (!isCancelled()) {
      refreshAssessmentCenter();
    }
  } finally {
    setAiResultsTableLoading(false);
  }
}
